import { Box, Stack, Typography } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { NotificationRecord } from '@/notifications';
import { NotificationCard } from './NotificationCard';
import { NotificationEmptyState } from './NotificationEmptyState';
import { NotificationSkeleton } from './NotificationSkeleton';

type GroupLabel = 'today' | 'yesterday' | 'earlierThisWeek' | 'older';

interface NotificationListProps {
  notifications: NotificationRecord[];
  isLoading: boolean;
  onRefresh: () => void;
  onMarkRead: (id: string) => void;
  onMarkUnread: (id: string) => void;
  onArchive: (id: string) => void;
  onDelete: (id: string) => void;
  onOpenRelatedProduct: (notification: NotificationRecord) => void;
  onViewDetails: (notification: NotificationRecord) => void;
}

interface NotificationRow {
  type: 'group' | 'item';
  group?: GroupLabel;
  notification?: NotificationRecord;
}

function groupKey(notification: NotificationRecord): GroupLabel {
  const createdAt = new Date(notification.createdAt).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.floor((Date.now() - createdAt) / dayMs);

  if (diffDays < 1) {
    return 'today';
  }

  if (diffDays < 2) {
    return 'yesterday';
  }

  if (diffDays < 7) {
    return 'earlierThisWeek';
  }

  return 'older';
}

function buildRows(notifications: NotificationRecord[]): NotificationRow[] {
  const groups = new Map<GroupLabel, NotificationRecord[]>();

  notifications.forEach((notification) => {
    const key = groupKey(notification);
    const current = groups.get(key) ?? [];
    current.push(notification);
    groups.set(key, current);
  });

  const orderedGroups: GroupLabel[] = [
    'today',
    'yesterday',
    'earlierThisWeek',
    'older',
  ];
  const rows: NotificationRow[] = [];

  orderedGroups.forEach((key) => {
    const items = groups.get(key);

    if (!items?.length) {
      return;
    }

    rows.push({ type: 'group', group: key });
    items.forEach((notification) => {
      rows.push({ type: 'item', notification });
    });
  });

  return rows;
}

function groupLabelKey(group: GroupLabel): string {
  if (group === 'today') {
    return 'notificationCenter.group.today';
  }

  if (group === 'yesterday') {
    return 'notificationCenter.group.yesterday';
  }

  if (group === 'earlierThisWeek') {
    return 'notificationCenter.group.earlierThisWeek';
  }

  return 'notificationCenter.group.older';
}

const itemRowHeight = 210;
const groupRowHeight = 40;
const overscanRows = 6;

export const NotificationList = memo(function NotificationList({
  notifications,
  isLoading,
  onRefresh,
  onMarkRead,
  onMarkUnread,
  onArchive,
  onDelete,
  onOpenRelatedProduct,
  onViewDetails,
}: NotificationListProps) {
  const { t } = useTranslation('entity-portal');
  const [scrollTop, setScrollTop] = useState(0);

  const rows = useMemo(() => buildRows(notifications), [notifications]);

  const totalHeight = useMemo(
    () =>
      rows.reduce((acc, row) => {
        return acc + (row.type === 'group' ? groupRowHeight : itemRowHeight);
      }, 0),
    [rows],
  );

  const offsets = useMemo(() => {
    let current = 0;

    return rows.map((row) => {
      const top = current;
      current += row.type === 'group' ? groupRowHeight : itemRowHeight;
      return top;
    });
  }, [rows]);

  const viewportHeight = 720;

  const [startIndex, endIndex] = useMemo(() => {
    let start = 0;
    let end = rows.length - 1;

    for (let index = 0; index < rows.length; index += 1) {
      const top = offsets[index];
      const rowHeight =
        rows[index].type === 'group' ? groupRowHeight : itemRowHeight;
      const bottom = top + rowHeight;

      if (bottom >= scrollTop) {
        start = Math.max(0, index - overscanRows);
        break;
      }
    }

    for (let index = start; index < rows.length; index += 1) {
      const top = offsets[index];

      if (top > scrollTop + viewportHeight) {
        end = Math.min(rows.length - 1, index + overscanRows);
        break;
      }
    }

    return [start, end] as const;
  }, [offsets, rows, scrollTop]);

  const visibleRows = rows.slice(startIndex, endIndex + 1);

  if (isLoading) {
    return <NotificationSkeleton />;
  }

  if (!rows.length) {
    return <NotificationEmptyState onRefresh={onRefresh} />;
  }

  return (
    <Box
      sx={{
        height: viewportHeight,
        overflowY: 'auto',
        p: 0,
      }}
      onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
      role="list"
      aria-label={t('notificationCenter.listAriaLabel')}
    >
      <Box sx={{ position: 'relative', height: totalHeight }}>
        {visibleRows.map((row, index) => {
          const rowIndex = startIndex + index;
          const top = offsets[rowIndex];

          if (row.type === 'group' && row.group) {
            return (
              <Typography
                key={`group-${row.group}-${rowIndex}`}
                variant="tableHeader"
                sx={{
                  position: 'absolute',
                  top,
                  left: 0,
                  right: 0,
                  px: 0,
                  py: 1,
                  bgcolor: 'background.paper',
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                }}
              >
                {t(groupLabelKey(row.group))}
              </Typography>
            );
          }

          const notification = row.notification as NotificationRecord;

          return (
            <Stack
              key={notification.id}
              sx={{
                position: 'absolute',
                top,
                left: 0,
                right: 0,
                p: 0,
              }}
            >
              <NotificationCard
                notification={notification}
                onMarkRead={onMarkRead}
                onMarkUnread={onMarkUnread}
                onArchive={onArchive}
                onDelete={onDelete}
                onOpenRelatedProduct={onOpenRelatedProduct}
                onViewDetails={onViewDetails}
              />
            </Stack>
          );
        })}
      </Box>
    </Box>
  );
});
